import { useId, useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import {
  ATTENTION_STATUSES,
  formatDate,
  formatDuration,
  formatRelative,
  lineActionLabel,
  lineStatusLabel,
  videoStatusLabel,
  type LineSummary,
} from '../../merchant/model.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  ButtonLink,
  Callout,
  Card,
  EmptyState,
  Eyebrow,
  PageHead,
  Pix,
  PixelCluster,
  PlusIcon,
  Poster,
  Tag,
} from '../../merchant/ui/primitives.js';

const ATTENTION_ORDER: Readonly<Record<string, number>> = {
  'needs-input': 0,
  failed: 1,
  'ready-to-choose': 2,
};

/**
 * Home, from the wireframe: a greeting, the one thing that needs the
 * merchant, three active Production Lines, recently completed videos and
 * the Business Profile summary — all from the shared snapshot.
 */
export function MerchantHomePage(): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const workspace = useWorkspace();
  const { businessName } = useMerchantWorkspaceIdentity();
  const navigate = useNavigate();
  const titleId = useId();
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;
  const hour = new Date().getHours();
  const greeting = `${copy.home.greet[hour < 12 ? 0 : hour < 17 ? 1 : 2]}${language === 'ar' ? '، ' : ', '}${workspaceName}`;
  const domain = (() => {
    const website = workspace.profile?.business.sources.find(
      (s) => s.platform === 'WEBSITE',
    );
    if (website === undefined) return '';
    try {
      return new URL(website.canonicalUrl).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  const attention = useMemo(() => {
    const candidates = workspace.lines.filter((line) =>
      ATTENTION_STATUSES.has(line.status),
    );
    return [...candidates].sort(
      (a, b) => (ATTENTION_ORDER[a.status] ?? 9) - (ATTENTION_ORDER[b.status] ?? 9),
    )[0];
  }, [workspace.lines]);

  const homeLines = useMemo(
    () =>
      workspace.lines
        .filter((line) => line.status !== 'draft' && line.status !== 'archived')
        .slice(0, 3),
    [workspace.lines],
  );
  const homeVideos = useMemo(
    () => workspace.videos.filter((video) => video.status === 'ready').slice(0, 3),
    [workspace.videos],
  );

  const payload = workspace.profile?.profile?.payload;
  const profileApproved = workspace.profile?.profile?.reviewState === 'APPROVED';

  const attentionDescription = (line: LineSummary): string => {
    if (line.action === 'open')
      return language === 'ar'
        ? 'افتح الخط لمراجعة حالة البحث أو النص المحفوظ. ملفك التجاري معتمد بالفعل.'
        : 'Open this line to review its saved research or script status. Your Business Profile is already approved.';
    if (line.status === 'failed') return copy.home.attentionFailed;
    if (line.status === 'ready-to-choose') return copy.home.attentionRecs;
    if (line.action === 'card') return copy.home.attentionCard;
    return copy.home.attentionProfile;
  };

  return (
    <section className="mj-ws-page" aria-labelledby={titleId} data-screen-label="Home">
      <PageHead
        eyebrow={
          <>
            {workspaceName}
            {domain ? (
              <>
                {' · '}
                <span dir="ltr">{domain}</span>
              </>
            ) : null}
          </>
        }
        title={greeting}
        titleId={titleId}
        intro={copy.home.intro}
        action={
          <Button
            variant="primary"
            onClick={() => void navigate('/production-lines/new')}
            disabled={workspace.creationDestination === null && !workspace.loading}
          >
            <PlusIcon />
            {copy.home.newLine}
          </Button>
        }
      />

      {workspace.failed ? (
        <Callout className="mj-ws-mt-24" role="status">
          <span className="mj-ws-callout-copy mj-ws-sm">
            {copy.home.summaryUnavailable}
          </span>
          <Button small onClick={() => void workspace.refresh()}>
            {copy.common.tryAgain}
          </Button>
        </Callout>
      ) : null}

      {attention !== undefined ? (
        <>
          <Eyebrow className="mj-ws-section-label">{copy.home.attention}</Eyebrow>
          <Card className="mj-ws-row is-attention" as="section">
            <Pix status={attention.status} />
            <div className="mj-ws-callout-copy">
              <div className="mj-ws-strong">{attention.title}</div>
              <div className="mj-ws-muted mj-ws-sm">
                {attentionDescription(attention)}
              </div>
            </div>
            <ButtonLink to={attention.actionHref}>
              {lineActionLabel(attention.action, copy)}
            </ButtonLink>
          </Card>
        </>
      ) : null}

      <Eyebrow className="mj-ws-section-label">{copy.home.activeLines}</Eyebrow>
      {workspace.loading ? (
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      ) : homeLines.length > 0 ? (
        <div className="mj-ws-home-lines">
          {homeLines.map((line) => (
            <Card key={line.id} as="article" className="mj-ws-col">
              <h3 className="mj-ws-h3">
                <Link to={line.href} dir="auto">
                  {line.title}
                </Link>
              </h3>
              <div className="mj-ws-row mj-ws-sm">
                <Pix status={line.status} />
                {lineStatusLabel(line.status, copy)}
              </div>
              <div className="mj-ws-row is-between">
                <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                  {formatRelative(line.updatedAt, copy, language)} ·{' '}
                  {copy.common.videosCount(line.videos)}
                </span>
                <ButtonLink to={line.actionHref} small>
                  {lineActionLabel(line.action, copy)}
                </ButtonLink>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          body={copy.home.noLines(workspaceName)}
          action={
            <Button
              variant="primary"
              onClick={() => void navigate('/production-lines/new')}
            >
              {copy.home.newLine}
            </Button>
          }
        >
          <PixelCluster />
        </EmptyState>
      )}

      <div className="mj-ws-g2 mj-ws-mt-32">
        <section aria-labelledby={`${titleId}-videos`}>
          <div className="mj-ws-row is-between mj-ws-mt-12">
            <Eyebrow id={`${titleId}-videos`}>{copy.home.recentVideos}</Eyebrow>
            <Link to="/videos" className="mj-ws-sm">
              {copy.home.viewAllVideos}
            </Link>
          </div>
          <Card padding={0} className="mj-ws-mt-12 mj-ws-vrows">
            {homeVideos.length > 0 ? (
              homeVideos.map((video) => (
                <button
                  key={video.id}
                  type="button"
                  className="mj-ws-vrow"
                  onClick={() =>
                    void navigate(
                      video.lineId === null
                        ? '/videos'
                        : `/videos/line/${encodeURIComponent(video.lineId)}?video=${encodeURIComponent(video.id)}`,
                    )
                  }
                >
                  <Poster mini posterUrl={video.posterUrl} state="playable" />
                  <span className="mj-ws-vrow-copy">
                    <span className="mj-ws-vrow-title" dir="auto">
                      {video.title}
                    </span>
                    <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
                      {video.lineTitle} ·{' '}
                      {formatDuration(video.durationSeconds) ?? copy.common.dash} ·{' '}
                      {formatDate(video.date, language)}
                    </span>
                  </span>
                  <Tag status={video.status}>
                    {videoStatusLabel(video.status, copy)}
                  </Tag>
                </button>
              ))
            ) : (
              <p className="mj-ws-muted mj-ws-sm" style={{ padding: '14px 20px' }}>
                {copy.home.noVideos}
              </p>
            )}
          </Card>
        </section>
        <section aria-labelledby={`${titleId}-profile`}>
          <Eyebrow id={`${titleId}-profile`} className="mj-ws-mt-12">
            {copy.home.profile}
          </Eyebrow>
          <Card className="mj-ws-mt-12">
            <p className="mj-ws-strong" dir="auto">
              {payload?.identity.value || workspaceName}
            </p>
            <p className="mj-ws-faint mj-ws-xs mj-ws-mt-8">
              {workspace.loading
                ? copy.common.loading
                : profileApproved
                  ? copy.home.profileConfirmed
                  : copy.home.profileDraft}
            </p>
            <p className="mj-ws-muted mj-ws-sm mj-ws-mt-12" dir="auto">
              {payload?.niche.value || copy.home.profileMissing}
            </p>
            {profileApproved ? (
              <p className="mj-ws-faint mj-ws-xs mj-ws-mt-12">
                {copy.home.profileShared}
              </p>
            ) : null}
            <ButtonLink to="/business-profile" small className="mj-ws-mt-16">
              {copy.home.reviewProfile}
            </ButtonLink>
          </Card>
        </section>
      </div>
    </section>
  );
}
